import * as animateScrollTo from 'animated-scroll-to';
import * as React from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Popup } from 'semantic-ui-react';

import analytics from '../../../../shared/services/analytics/analytics';
import PerformanceComparison from '../../../../containers/PerformanceComparison/PerformanceComparison';
import SelectedAlphas from '../../../../containers/SelectedAlphas/SelectedAlphas';
import { localeData } from '../../../../locales/data';
import { StateType } from '../../../../reducers';
import {
  getUserIdSelector,
  getUserPermissionSet,
  getUserPermissionsSelector,
} from '../../../../reducers/AuthenticationReducer/AuthenticationSelector';
import { activeTeamSelector } from '../../../../reducers/TeamReducer/ActiveTeamReducer/ActiveTeamSelector';
import { addModifiers } from '../../../../shared/helpers';
import { getLocalStorageItem, setLocalStorageItem } from '../../../../shared/helpers/local-storage';
import ChartContainer from '../../../AlphaChart/ChartContainer';
import AlphaCodeContainer from '../../../AlphaCode/AlphaCodeContainer';
import AlphaCorrelation from '../../../AlphaCorrelation/AlphaCorrelation';
import { AlphaProperties } from '../../../AlphaProperties';
import AlphaSummary from '../../../AlphaSummary/AlphaSummary';
import AlphaTestingStatus from '../../../AlphaTestingStatus/AlphaTestingStatus';
import { withAlphaChartData } from '../../../AlphaChart/HOCAlphaChart';

import './AlphasDetailsContent.scss';
import { SettingsSection } from './SettingsSection';

import { AlphaTutorialNotification } from '../../../AlphaTutorialNotification/AlphaTutorialNotification';
import { Permission } from '../../../../types';
import { getTutorialStatus } from '../../../../reducers/TutorialReducer/TutorialSelectors';
import { TutorialStatus } from '../../../../shared/api/api';
import { ConsultantAlphaTutorialNotification } from '../../../ConsultantAlphaTutorialNotification/ConsultantAlphaTutorialNotification';
import { getConsultantTutorialStatus } from '../../../../reducers/ConsultantTutorialReducer/ConsultantTutorialSelectors';

const AlphaChart = React.memo(withAlphaChartData(ChartContainer));

interface AlphasDetailsContentStateProps {
  userID: string;
  activeTeam: any;
  permissions: any;
  permissionSet: Set<Permission>;
  tutorialStatus?: TutorialStatus;
  consultantTutorialStatus?: TutorialStatus;
  stage: string;
  alphaType: string;
}

interface AlphasDetailsContentProps {
  activeAlphaInfo: any;
  editorWillMount: (monaco: any) => void;
  handleCloneAlphaFromContent: () => void;
  typeStage: string;
  tabsStage: any;
  handleChangeTab: (val: string) => void;
  isConsultant: boolean;
  recordSets: any;
  recordSetsOption: string;
  handleChangeRecordSets: (e: any, data: any) => void;
  isFetchingRecordSetChart: boolean;
  refreshCorrelationSelf: (e: any) => void;
  refreshCorrelationProd: (e: any) => void;
  hasVisualization: boolean;
  path: string[];
  itemsStoredName: string;
  showAlphaCode: boolean;
  showCode?: boolean;
  showLearn?: boolean;
  isSimulation?: boolean;
  shouldResize?: number;
  modifiers?: string[];
  isSimulate?: boolean;
  tabIndex?: number;
  indexChild?: number | undefined;
  isMultiTab?: boolean;
  size?: number;
  isOthers: boolean;
  isTestPeriodControlShown?: boolean;
  isTestPeriodShown: boolean;
}

interface AlphasDetailsContentState {
  selectedSection: number;
  doSelectedSection: boolean;
  sectionDetailsOrder: string[];
  settingsOpen: boolean;
}

interface MenuAlphaDetailsProps {
  actions: string[];
  handleScrollTo: (elem: string, index: number) => void;
  handleSettings: () => void;
  selectedSection: number;
  settingsOpen: boolean;
}

const mapActionsToLabel: any = {
  code: 'Code',
  chart: 'Chart',
  summary: 'Summary',
  correlation: 'Correlation',
  testingStatus: 'Testing Status',
  selectedAlphas: 'Selected Alphas',
  performanceComparison: 'Performance Comparison',
  properties: 'Properties',
};

const noShowStatuses: Set<TutorialStatus> = new Set(['COMPLETED', 'EXPIRED']);

const MenuAlphaDetails = ({ handleScrollTo, selectedSection, actions, handleSettings, settingsOpen }: MenuAlphaDetailsProps) => (
  <div className="alphas-details__actions">
    <div className="alphas-details__actions-list">
      {actions.map((elem: string, index: number) => {
        const handleScrollToLocal = (event: any) => {
          event.preventDefault();
          handleScrollTo(elem, index);
        };
        const tabModifiers = index === selectedSection ? [elem, 'active'] : [elem];

        return (
          <Popup
            key={`alphas-details__actions-${index}`}
            content={mapActionsToLabel[elem]}
            position="left center"
            trigger={
              <button
                className={addModifiers('alphas-details__actions-item', tabModifiers)}
                onClick={handleScrollToLocal}
                disabled={settingsOpen}
              />
            }
          />
        );
      })}
    </div>
    <Popup
      content="Settings"
      position="left center"
      trigger={<button className="alphas-details__action-settings" onClick={handleSettings} />}
    />
  </div>
);

type AlphasDetailsContentPropsGeneric = AlphasDetailsContentProps & AlphasDetailsContentStateProps & InjectedIntlProps;

class AlphasDetailsContent extends React.Component<AlphasDetailsContentPropsGeneric, AlphasDetailsContentState> {
  constructor(props: AlphasDetailsContentPropsGeneric) {
    super(props);
    this.state = {
      selectedSection: 0,
      sectionDetailsOrder: [...this.getItemsLocalStore()],
      settingsOpen: false,
      doSelectedSection: true,
    };
  }

  componentDidMount() {
    this.updateSectionDetailsOrder();
  }

  componentDidUpdate(prevProps: AlphasDetailsContentPropsGeneric) {
    if (prevProps.activeTeam !== this.props.activeTeam) {
      this.updateSectionDetailsOrder();
    }
    if (prevProps.stage !== this.props.stage) {
      this.updateSectionDetailsOrder();
    }

    if (prevProps.alphaType !== this.props.alphaType) {
      this.updateSectionDetailsOrder();
    }
  }

  updateSectionDetailsOrder = (items: any = this.state.sectionDetailsOrder) => {
    let newItems = [...items];
    if (this.props.alphaType === 'SUPER') {
      if (!newItems.includes('selectedAlphas')) {
        const indexTestingStatus: number = newItems.findIndex((section: string) => section === 'testingStatus');
        newItems.splice(indexTestingStatus + 1, 0, 'selectedAlphas');
        this.setState({ sectionDetailsOrder: newItems });
        setLocalStorageItem(this.props.userID, this.props.itemsStoredName, newItems);
      }
    } else {
      if (newItems.includes('selectedAlphas')) {
        newItems = newItems.filter((section: string) => section !== 'selectedAlphas');
        this.setState({ sectionDetailsOrder: newItems });
        setLocalStorageItem(this.props.userID, this.props.itemsStoredName, newItems);
      }
    }
    if (this.props.stage === 'IS') {
      if (!newItems.includes('performanceComparison')) {
        const indexTestingStatus: number = newItems.findIndex((section: string) => section === 'testingStatus');
        newItems.splice(indexTestingStatus + 1, 0, 'performanceComparison');
        this.setState({ sectionDetailsOrder: newItems });
        setLocalStorageItem(this.props.userID, this.props.itemsStoredName, newItems);
      }
    } else {
      if (newItems.includes('performanceComparison')) {
        newItems = newItems.filter((section: string) => section !== 'performanceComparison');
        this.setState({ sectionDetailsOrder: newItems });
        setLocalStorageItem(this.props.userID, this.props.itemsStoredName, newItems);
      }
    }
  };

  resetOrder = () => {
    const defaultItems = this.props.showAlphaCode
      ? ['code', 'chart', 'summary', 'correlation', 'testingStatus', 'selectedAlphas', 'performanceComparison', 'properties']
      : ['chart', 'summary', 'correlation', 'testingStatus', 'selectedAlphas', 'performanceComparison', 'properties'];

    this.updateSectionDetailsOrder(defaultItems);
  };

  getItemsLocalStore = (): string[] => {
    const defaultItems = this.props.showAlphaCode
      ? ['code', 'chart', 'summary', 'correlation', 'testingStatus', 'selectedAlphas', 'performanceComparison', 'properties']
      : ['chart', 'summary', 'correlation', 'testingStatus', 'selectedAlphas', 'performanceComparison', 'properties'];

    const itemsStored: any = getLocalStorageItem(this.props.userID, this.props.itemsStoredName);
    return itemsStored ? itemsStored : defaultItems;
  };

  mapSectionsToComponent() {
    const components = {
      summary: AlphaSummary,
      correlation: AlphaCorrelation,
      chart: AlphaChart,
      properties: AlphaProperties,
      testingStatus: AlphaTestingStatus,
      performanceComparison: PerformanceComparison,
      selectedAlphas: SelectedAlphas,
    };

    return this.props.showAlphaCode ? { ...components, code: AlphaCodeContainer } : components;
  }

  scrollToTop() {
    const elemScroll: any = document.getElementById('alphas-details-sections');
    elemScroll.scrollTop = 0;
  }

  handleSettings = () => {
    analytics.trackCustomEvent('sim_click_results_section', { event_label: 'settings' });
    this.scrollToTop();
    this.setState((prevState: any) => ({ settingsOpen: !prevState.settingsOpen }));
  };

  handleScrollTo = (elem: string, index: number) => {
    analytics.trackCustomEvent('sim_click_results_section', { event_label: elem });
    const sectionToScroll: any = document.getElementById(`alphas-${elem}`);
    const elemScroll: any = document.getElementById('alphas-details-sections');
    const onComplete = () => this.setState({ doSelectedSection: true });
    if (sectionToScroll && elemScroll) {
      this.setState(
        { selectedSection: index, doSelectedSection: false },
        animateScrollTo(sectionToScroll.offsetTop, { element: elemScroll, onComplete })
      );
    }
  };

  handleScroll = (_: any) => {
    const { selectedSection, doSelectedSection } = this.state;
    const hiddenSections = getHiddenSections({
      isOthers: this.props.isOthers,
      isTestPeriodShown: this.props.isTestPeriodShown,
      stage: this.props.stage,
      permissionSet: this.props.permissionSet,
    });

    const elemScroll: any = document.getElementById('alphas-details-sections');
    const elemScrollTop: number = elemScroll.scrollTop;
    const elemScrollHeight: number = elemScroll.offsetHeight;
    const sections: string[] = this.state.sectionDetailsOrder.filter((section: string) => !hiddenSections.has(section));

    if (elemScrollTop + elemScrollHeight >= elemScroll.scrollHeight - 1) {
      this.setState({ selectedSection: sections.length - 1 });
    } else {
      sections.forEach((section: string, index: number) => {
        const currentSection: any = document.getElementById(`alphas-${section}`);
        const compensation = 1;
        const currentSectionHeight: number = currentSection.offsetHeight - compensation;
        const currentSectionTop: number = currentSection.offsetTop;
        if (elemScrollTop >= currentSectionTop && elemScrollTop <= currentSectionTop + currentSectionHeight) {
          if (selectedSection !== index && doSelectedSection) {
            this.setState({ selectedSection: index });
          }
        }
      });
    }
  };

  handleReorder = (items: string[]) => {
    this.setState(
      {
        sectionDetailsOrder: items,
        settingsOpen: false,
      },
      () => this.scrollToTop()
    );
    this.scrollToTop();
    setLocalStorageItem(this.props.userID, this.props.itemsStoredName, items);
  };

  getSummary() {
    return {
      title: this.props.intl.formatMessage({ id: 'alpha.summary', defaultMessage: localeData.en['alpha.summary'] }),
      renderGrade: true,
      renderTheme: true,
      renderClassification: true,
      renderPyramids: true,
    };
  }

  getTestingStatus() {
    return {
      title: this.props.intl.formatMessage({ id: 'testing.status', defaultMessage: localeData.en['testing.status'] }),
      isStageChooserHidden: true,
    };
  }

  getComponentProps(componentProps: string) {
    const props: any = {
      summary: this.getSummary(),
      testingStatus: this.getTestingStatus(),
      chart: { size: this.props.size },
      performanceComparison: { size: this.props.size },
    };

    return props.hasOwnProperty(componentProps) ? props[componentProps] : {};
  }

  render() {
    const components: any = this.mapSectionsToComponent();

    const { modifiers = [] } = this.props;
    if (this.props.isOthers) {
      modifiers.push('no-footer');
    }

    const hiddenSections = getHiddenSections({
      isOthers: this.props.isOthers,
      isTestPeriodShown: this.props.isTestPeriodShown,
      stage: this.props.stage,
      permissionSet: this.props.permissionSet,
    });

    const orderedShownSections = this.state.sectionDetailsOrder.filter((section: string) => !hiddenSections.has(section));
    const isWithTutorialPermission = this.props.permissionSet.has('TUTORIAL');

    const isUserTutorialShown =
      this.props.isSimulate && isWithTutorialPermission && !(this.props.tutorialStatus && noShowStatuses.has(this.props.tutorialStatus));
    const isConsultantTutorialShown =
      this.props.isSimulate &&
      this.props.permissionSet.has('CONSULTANT') &&
      !(this.props.consultantTutorialStatus && noShowStatuses.has(this.props.consultantTutorialStatus));

    return (
      <div className={addModifiers('alphas-details__data', modifiers)}>
        <MenuAlphaDetails
          handleScrollTo={this.handleScrollTo}
          selectedSection={this.state.selectedSection}
          actions={orderedShownSections}
          handleSettings={this.handleSettings}
          settingsOpen={this.state.settingsOpen}
        />
        <div className="alphas-details-sections" onScroll={this.handleScroll} id="alphas-details-sections">
          <div className="alphas-details-sections__content">
            {isUserTutorialShown && <AlphaTutorialNotification alphaPath={this.props.path} />}
            {isConsultantTutorialShown && <ConsultantAlphaTutorialNotification alphaPath={this.props.path} />}

            {orderedShownSections.map((section: string, index: number) => {
              const Component = components[section];

              return (
                <Component
                  key={`details-${section}-${index}`}
                  path={this.props.path}
                  id={`alphas-${section}`}
                  fatherClass=".alphas-details-sections"
                  showCode={this.props.showCode}
                  showLearn={this.props.showLearn}
                  isSimulation={this.props.isSimulation}
                  shouldResize={this.props.shouldResize}
                  isSimulate={this.props.isSimulate}
                  tabIndex={this.props.tabIndex}
                  indexChild={this.props.indexChild}
                  isMultiTab={this.props.isMultiTab}
                  position={(index + 1) % 2 === 0 ? 'even' : 'odd'}
                  haveResults={true}
                  {...this.getComponentProps(section)}
                />
              );
            })}

            <SettingsSection
              settingsStatus={this.state.settingsOpen}
              sectionDetailsOrder={this.state.sectionDetailsOrder}
              handleReorder={this.handleReorder}
              resetOrder={this.resetOrder}
            />
          </div>
        </div>
      </div>
    );
  }
}
interface HiddenSectionsParameters {
  isOthers: boolean;
  isTestPeriodShown: boolean;
  stage: string;
  permissionSet: Set<Permission>;
}

function getHiddenSections({ isOthers, isTestPeriodShown, stage, permissionSet }: HiddenSectionsParameters) {
  const otherHiddenSections = ['correlation', 'selectedAlphas', 'properties'];
  const testPeriodHiddenSections = ['correlation', 'testingStatus', 'performanceComparison'];
  const isOs = stage === 'OS';
  const osHiddenSections = ['testingStatus'];
  const isPerformanceComparisonHidden = !permissionSet.has('BEFORE_AND_AFTER_PERFORMANCE_V2');
  const hiddenPerformanceComparison = ['performanceComparison'];
  return new Set([
    ...(isOthers ? otherHiddenSections : []),
    ...(!isTestPeriodShown ? testPeriodHiddenSections : []),
    ...(isOs ? osHiddenSections : []),
    ...(isPerformanceComparisonHidden ? hiddenPerformanceComparison : []),
  ]);
}

const mapStateToProps = (state: StateType, props: AlphasDetailsContentProps): AlphasDetailsContentStateProps => ({
  // @ts-ignore
  userID: getUserIdSelector(state),
  activeTeam: activeTeamSelector(state),
  permissions: getUserPermissionsSelector(state),
  permissionSet: getUserPermissionSet(state),
  tutorialStatus: getTutorialStatus(state),
  consultantTutorialStatus: getConsultantTutorialStatus(state),
  // @ts-ignore
  stage: state.getIn(props.path.concat(['alphaDetail', 'stage'])),
  // @ts-ignore
  alphaType: state.getIn(props.path.concat(['alphaDetail', 'type'])),
  isTestPeriodShown:
    state.getIn(props.path.concat(['alphaDetail', 'isTestPeriodShown'])) ??
    !(
      state.getIn(props.path.concat(['alphaDetail', 'status'])) === 'UNSUBMITTED' &&
      state.getIn(props.path.concat(['alphaDetail', 'test', 'startDate']))
    ),
});

export default compose<any>(
  connect<AlphasDetailsContentStateProps, any, any, StateType>(mapStateToProps, null),
  injectIntl
)(AlphasDetailsContent);
